/**
 * Time-phased financial layer (Planisware FIN peer of hours calendar).
 * Feasibility money — not a general ledger.
 */

export type CostCategory =
  | 'labor'
  | 'external_labor'
  | 'materials'
  | 'contracts'
  | 'equipment'
  | 'travel'
  | 'facilities'
  | 'other';

export type CapexOpex = 'capex' | 'opex' | 'unspecified';

export type CostPhasingMode = 'launch_relative' | 'absolute_periods';

export interface FundingEnvelope {
  id: string;
  projectId: string;
  scenarioId: string;
  name: string;
  currency: string;
  owningOrgUnitId?: string;
  strategicCategory?: string;
  fundingSource?: string;
  notes?: string;
  periods: FundingPeriod[];
}

export interface FundingPeriod {
  id?: string;
  envelopeId: string;
  bucketStart: string;
  bucketEnd: string;
  approvedAmount: number;
  forecastAmount?: number | null;
}

export interface PriorityCostPlan {
  id: string;
  projectId: string;
  priorityId: string;
  name?: string;
  currency: string;
  costCategory: CostCategory;
  capexOpex: CapexOpex;
  estimateAmount?: number | null;
  approvedAmount?: number | null;
  forecastAmount: number;
  commitmentAmount?: number | null;
  actualAmount?: number | null;
  phasingMode: CostPhasingMode;
  offsetStartWeeks: number;
  offsetEndWeeks: number;
  laborHours?: number | null;
  hourlyRate?: number | null;
  notes?: string;
  periods?: PriorityCostPeriod[];
}

export interface PriorityCostPeriod {
  id?: string;
  costPlanId: string;
  bucketStart: string;
  bucketEnd: string;
  forecastAmount: number;
  approvedAmount?: number | null;
}

export interface CostDemandChunk {
  priorityId: string;
  priorityName: string;
  costPlanId: string;
  costCategory: CostCategory;
  bucketStart: string;
  bucketEnd: string;
  amount: number;
}

export interface BucketMoneyUtilization {
  bucketStart: string;
  bucketEnd: string;
  /** Sum of approved envelope amounts for the bucket */
  approvedSupply: number;
  /** Forecast demand from placed launches */
  forecastDemand: number;
  utilizationPct: number;
  overage: number;
}

export interface FundingAffordability {
  withinEnvelope: boolean;
  totalApproved: number;
  totalForecastDemand: number;
  overageTotal: number;
  /** Priorities contributing most to overage periods */
  contributingPriorities: {
    priorityId: string;
    priorityName: string;
    forecastDemand: number;
  }[];
  overloadedBuckets: {
    bucketStart: string;
    overage: number;
    demand: number;
    approved: number;
  }[];
}

export interface TimePhasedFinancialOutput {
  moneyUtilization: BucketMoneyUtilization[];
  affordability: FundingAffordability;
  demandChunks: CostDemandChunk[];
}

export const COST_CATEGORIES: { key: CostCategory; label: string }[] = [
  { key: 'labor', label: 'Labor' },
  { key: 'external_labor', label: 'External labor' },
  { key: 'materials', label: 'Materials' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'travel', label: 'Travel' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'other', label: 'Other' },
];
